import { useState, useEffect } from "react";
import { useMutation } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { 
  AlertTriangle, 
  ArrowLeft, 
  ArrowRight, 
  Trash2, 
  UserMinus, 
  Building2,
  Loader2,
  CheckCircle2
} from "lucide-react";
import { cn } from "@/lib/utils";
import { useTranslation } from "react-i18next";
import type { UserInfo, UserRole } from "@/types/superAdmin";

interface DeleteUserDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  user: UserInfo | null;
  onDeleteSuccess: () => void;
}

type DeleteMode = "partial" | "complete";
type Step = "mode" | "gym" | "preview";

interface DryRunResult {
  deletedByTable: Record<string, number>;
  tableLabels: Record<string, string>;
  storageResults: Array<{ bucket: string; prefix: string; removed: number }>;
}

export function DeleteUserDialog({ open, onOpenChange, user, onDeleteSuccess }: DeleteUserDialogProps) {
  const { t } = useTranslation(["superadmin", "common"]);
  const [step, setStep] = useState<Step>("mode");
  const [deleteMode, setDeleteMode] = useState<DeleteMode>("partial");
  const [selectedGymId, setSelectedGymId] = useState<string>("");
  const [confirmChecked, setConfirmChecked] = useState(false);
  const [dryRunResult, setDryRunResult] = useState<DryRunResult | null>(null);

  // Reset state when dialog opens/closes
  useEffect(() => {
    if (open) {
      setStep("mode");
      setDeleteMode("partial");
      setSelectedGymId("");
      setConfirmChecked(false);
      setDryRunResult(null);
    }
  }, [open]);

  // Get unique gyms from user roles
  const userGyms = user?.roles
    .filter((r): r is UserRole & { gym_id: string; gym_name: string } => 
      !!r.gym_id && !!r.gym_name
    )
    .reduce((acc, role) => {
      if (!acc.find(g => g.gym_id === role.gym_id)) {
        acc.push({ gym_id: role.gym_id, gym_name: role.gym_name, city: role.city });
      }
      return acc;
    }, [] as Array<{ gym_id: string; gym_name: string; city?: string }>) || [];

  // Dry run mutation
  const dryRunMutation = useMutation({
    mutationFn: async ({ userId, gymId }: { userId: string; gymId?: string }) => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error("No session");

      const { data, error } = await supabase.functions.invoke("delete-user-everywhere", {
        headers: { Authorization: `Bearer ${session.access_token}` },
        body: { userId, gymId, dryRun: true },
      });

      if (error) throw error;
      return data;
    },
    onSuccess: (data) => {
      setDryRunResult(data.summary);
    },
  });

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: async ({ userId, gymId }: { userId: string; gymId?: string }) => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error("No session");

      const { data, error } = await supabase.functions.invoke("delete-user-everywhere", {
        headers: { Authorization: `Bearer ${session.access_token}` },
        body: { userId, gymId, dryRun: false },
      });

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      onDeleteSuccess();
      onOpenChange(false);
    },
  });

  const handleNext = () => {
    if (step === "mode") {
      if (deleteMode === "complete") {
        // Go directly to preview for complete deletion
        dryRunMutation.mutate({ userId: user!.user_id });
        setStep("preview");
      } else {
        // Go to gym selection for partial deletion
        setStep("gym");
      }
    } else if (step === "gym") {
      dryRunMutation.mutate({ userId: user!.user_id, gymId: selectedGymId });
      setStep("preview");
    }
  };

  const handleBack = () => {
    if (step === "preview") {
      setDryRunResult(null);
      setStep(deleteMode === "complete" ? "mode" : "gym");
    } else if (step === "gym") {
      setStep("mode");
    }
  };

  const handleDelete = () => {
    if (!user) return;
    const gymId = deleteMode === "partial" ? selectedGymId : undefined;
    deleteMutation.mutate({ userId: user.user_id, gymId });
  };

  const fullName = user ? `${user.first_name || ""} ${user.last_name || ""}`.trim() || user.email : "";

  // Calculate total items to be deleted
  const totalItems = dryRunResult
    ? Object.values(dryRunResult.deletedByTable).reduce((sum, count) => sum + Math.max(0, count), 0)
    : 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Trash2 className="h-5 w-5 text-destructive" />
            {t("deleteUser.title")}
          </DialogTitle>
          <DialogDescription>
            {fullName} ({user?.email})
          </DialogDescription>
        </DialogHeader>

        {/* Step 1: Mode Selection */}
        {step === "mode" && (
          <div className="space-y-4 py-4">
            <RadioGroup value={deleteMode} onValueChange={(v) => setDeleteMode(v as DeleteMode)}>
              <div 
                className={cn(
                  "flex items-start space-x-3 p-4 rounded-lg border cursor-pointer transition-colors",
                  deleteMode === "partial" ? "border-primary bg-primary/5" : "border-border hover:border-primary/50"
                )}
                onClick={() => setDeleteMode("partial")}
              >
                <RadioGroupItem value="partial" id="partial" className="mt-1" />
                <div className="space-y-1">
                  <Label htmlFor="partial" className="flex items-center gap-2 cursor-pointer">
                    <UserMinus className="h-4 w-4" />
                    {t("deleteUser.partialTitle")}
                  </Label>
                  <p className="text-sm text-muted-foreground">
                    {t("deleteUser.partialDesc")}
                  </p>
                </div>
              </div>

              <div 
                className={cn(
                  "flex items-start space-x-3 p-4 rounded-lg border cursor-pointer transition-colors",
                  deleteMode === "complete" ? "border-destructive bg-destructive/5" : "border-border hover:border-destructive/50"
                )}
                onClick={() => setDeleteMode("complete")}
              >
                <RadioGroupItem value="complete" id="complete" className="mt-1" />
                <div className="space-y-1">
                  <Label htmlFor="complete" className="flex items-center gap-2 cursor-pointer">
                    <Trash2 className="h-4 w-4 text-destructive" />
                    {t("deleteUser.completeTitle")}
                  </Label>
                  <p className="text-sm text-muted-foreground">
                    <span className="text-destructive font-medium">⚠️ {t("deleteUser.irreversible")}</span> — {t("deleteUser.deletesAccountAndData")}
                  </p>
                </div>
              </div>
            </RadioGroup>
          </div>
        )}

        {/* Step 2: Gym Selection */}
        {step === "gym" && (
          <div className="space-y-4 py-4">
            <p className="text-sm text-muted-foreground">
              {t("deleteUser.selectGym")}
            </p>
            <RadioGroup value={selectedGymId} onValueChange={setSelectedGymId}>
              {userGyms.map((gym) => (
                <div 
                  key={gym.gym_id}
                  className={cn(
                    "flex items-center space-x-3 p-3 rounded-lg border cursor-pointer transition-colors",
                    selectedGymId === gym.gym_id ? "border-primary bg-primary/5" : "border-border hover:border-primary/50"
                  )}
                  onClick={() => setSelectedGymId(gym.gym_id)}
                >
                  <RadioGroupItem value={gym.gym_id} id={gym.gym_id} />
                  <Label htmlFor={gym.gym_id} className="flex items-center gap-2 cursor-pointer flex-1">
                    <Building2 className="h-4 w-4 text-muted-foreground" />
                    <span>{gym.gym_name}</span>
                    {gym.city && <span className="text-muted-foreground">({gym.city})</span>}
                  </Label>
                </div>
              ))}
            </RadioGroup>

            {userGyms.length === 1 && (
              <Alert variant="destructive">
                <AlertTriangle className="h-4 w-4" />
                <AlertDescription>
                  {t("deleteUser.onlyOneGymWarning")}
                </AlertDescription>
              </Alert>
            )}
          </div>
        )}

        {/* Step 3: Preview (Dry Run) */}
        {step === "preview" && (
          <div className="space-y-4 py-4">
            {dryRunMutation.isPending ? (
              <div className="space-y-3">
                <Skeleton className="h-4 w-3/4" />
                <Skeleton className="h-24 w-full" />
                <Skeleton className="h-4 w-1/2" />
              </div>
            ) : dryRunMutation.isError ? (
              <Alert variant="destructive">
                <AlertTriangle className="h-4 w-4" />
                <AlertDescription>
                  {t("deleteUser.previewError")} {dryRunMutation.error?.message}
                </AlertDescription>
              </Alert>
            ) : dryRunResult ? (
              <>
                <Alert variant={deleteMode === "complete" ? "destructive" : "default"}>
                  <AlertTriangle className="h-4 w-4" />
                  <AlertDescription>
                    {deleteMode === "complete" 
                      ? t("deleteUser.completeWarning")
                      : t("deleteUser.partialWarning")
                    }
                  </AlertDescription>
                </Alert>

                <div className="space-y-2">
                  <h4 className="font-medium text-sm">{t("deleteUser.dataToDelete")}</h4>
                  <ScrollArea className="h-48 rounded-lg border">
                    <div className="p-3 space-y-1">
                      {Object.entries(dryRunResult.deletedByTable)
                        .filter(([_, count]) => count > 0)
                        .sort((a, b) => b[1] - a[1])
                        .map(([table, count]) => (
                          <div key={table} className="flex justify-between py-1 text-sm">
                            <span className="text-muted-foreground">
                              {dryRunResult.tableLabels[table] || table}
                            </span>
                            <span className="font-medium">{count}</span>
                          </div>
                        ))
                      }
                      {totalItems === 0 && (
                        <p className="text-sm text-muted-foreground text-center py-4">
                          {t("deleteUser.noDataToDelete")}
                        </p>
                      )}
                    </div>
                  </ScrollArea>
                </div>

                {deleteMode === "complete" && (
                  <div className="flex items-center gap-2 p-3 rounded-lg bg-destructive/10 border border-destructive/20">
                    <span className="text-sm">🔐 {t("deleteUser.authAccount")}</span>
                    <span className="text-sm font-medium text-destructive">{t("deleteUser.willBeDeleted")}</span>
                  </div>
                )}

                <div className="flex items-center space-x-2 pt-2">
                  <Checkbox 
                    id="confirm" 
                    checked={confirmChecked}
                    onCheckedChange={(checked) => setConfirmChecked(checked === true)}
                  />
                  <Label htmlFor="confirm" className="text-sm cursor-pointer">
                    {t("deleteUser.understandIrreversible")}
                  </Label>
                </div>
              </>
            ) : null}
          </div>
        )}

        <DialogFooter className="gap-2 sm:gap-0">
          {step !== "mode" && (
            <Button variant="outline" onClick={handleBack} disabled={deleteMutation.isPending}>
              <ArrowLeft className="h-4 w-4 mr-2" />
              {t("common:back")}
            </Button>
          )}

          {step === "mode" && (
            <Button onClick={handleNext}>
              {t("common:next")}
              <ArrowRight className="h-4 w-4 ml-2" />
            </Button>
          )}

          {step === "gym" && (
            <Button onClick={handleNext} disabled={!selectedGymId}>
              {t("deleteUser.preview")}
              <ArrowRight className="h-4 w-4 ml-2" />
            </Button>
          )}

          {step === "preview" && (
            <Button
              variant="destructive"
              onClick={handleDelete}
              disabled={!confirmChecked || deleteMutation.isPending || dryRunMutation.isPending}
            >
              {deleteMutation.isPending ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  {t("deleteUser.deleting")}
                </>
              ) : (
                <>
                  <Trash2 className="h-4 w-4 mr-2" />
                  {deleteMode === "complete" ? t("deleteUser.deletePermanently") : t("deleteUser.removeFromGym")}
                </>
              )}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
